'use client';
import { jsx } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import cl from 'clsx/lite';
import { forwardRef, useRef, useContext, useState, useEffect } from 'react';
import '@digdir/designsystemet-web';
import { useMergeRefs } from '../../utilities/hooks/use-merge-refs/use-merge-refs.js';
import { Context } from './popover-trigger-context.js';

/**
 * Popover component, used to display content in a popover over an element.
 *
 * @example with TriggerContext
 * <Popover.TriggerContext>
 *   <Popover.Trigger>Open Popover</Popover.Trigger>
 *   <Popover>
 *     Content
 *   </Popover>
 * </Popover.TriggerContext>
 *
 * @example without TriggerContext
 * <Button popovertarget="my-popover">Open Popover</Button>
 * <Popover id="my-popover">
 *   Content
 * </Popover>
 */
const Popover = forwardRef(function Popover({ id, className, onClose, onOpen, open, variant = 'default', placement = 'top', autoPlacement = true, asChild = false, ...rest }, ref) {
    const Component = asChild ? Slot : 'div';
    const popoverRef = useRef(null);
    const mergedRefs = useMergeRefs([popoverRef, ref]);
    const { popoverId, setPopoverId } = useContext(Context);
    const [internalOpen, setInternalOpen] = useState(false);
    const controlledOpen = open ?? internalOpen;
    // NOTE: This code is purely to add React controlled component ability to Popover API
    useEffect(() => {
        const popover = popoverRef.current;
        const trigger = `[popovertarget="${popover?.id}"],[commandfor="${popover?.id}"]`;
        const handleClick = (event) => {
            const el = event.target;
            const isTrigger = el?.closest?.(trigger);
            const isOutside = !isTrigger && !popover?.contains(el);
            if (isTrigger)
                event.preventDefault(); // Prevent native Popover API
            if (controlledOpen && (isTrigger || isOutside)) {
                setInternalOpen(false);
                onClose?.();
            }
            else if (!controlledOpen && isTrigger) {
                setInternalOpen(true);
                onOpen?.();
            }
        };
        const handleKeydown = (event) => {
            if (event.key !== 'Escape' || !controlledOpen)
                return;
            const isOpen = popoverRef.current?.matches(':popover-open') ||
                popoverRef.current?.classList.contains(':popover-open'); // Polyfill support
            if (!isOpen)
                return;
            event.preventDefault(); // Prevent closing fullscreen in Safari
            document.querySelector(trigger)?.focus?.(); // Move focus back to trigger since `popoover="manual"` doesn't do this
            setInternalOpen(false);
            onClose?.();
        };
        popover?.togglePopover?.(controlledOpen);
        if (controlledOpen) {
            const options = { detail: document.querySelector(trigger) };
            popover?.dispatchEvent(new CustomEvent('ds-toggle-source', options)); // Since togglePopover({ source }) is not supported in all browsers yet
        }
        document.addEventListener('click', handleClick, true); // Use capture to execute before React event API
        document.addEventListener('keydown', handleKeydown);
        return () => {
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [controlledOpen]);
    // Update context with id
    useEffect(() => {
        if (id)
            setPopoverId?.(id);
    }, [id]);
    return (jsx(Component, { className: cl('ds-popover', className), id: id || popoverId, popover: 'manual', "data-placement": placement, "data-variant": variant, ref: mergedRefs, suppressHydrationWarning // Since _ds-floating adds attributes
        : true, ...rest }));
});

export { Popover };
